import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux'
import type { UserRole, Runner, LegAssignment, LegLog, LocationPoint, RaceState as RaceStateType, TimerState, Notification } from '../types/index'

// UI Slice
const uiSlice = createSlice({
  name: 'ui',
  initialState: {
    activeTab: 'map' as 'map' | 'team' | 'race' | 'log' | 'admin',
    isAuthenticated: !!localStorage.getItem('auth_token'),
    userRole: (localStorage.getItem('user_role') as UserRole | null) || null,
    username: localStorage.getItem('username') || null,
    token: localStorage.getItem('auth_token') || null,
    loggedInRunnerId: localStorage.getItem('runner_id') ? parseInt(localStorage.getItem('runner_id')!) : null as number | null,
    loggedInRunnerNumber: localStorage.getItem('runner_number') ? parseInt(localStorage.getItem('runner_number')!) : null as number | null,
    isNearHandoff: false,
    notifications: [] as Notification[],
    pollingInterval: 300000 as 5000 | 15000 | 300000,
    testGlowActive: false,
    testPaceMultiplier: 1 as 1 | 2 | 3 | 4,
    isOffline: false,
    consecutivePollFailures: 0,
  },
  reducers: {
    setAuthenticated(state, action: PayloadAction<{ token: string; role: UserRole; username?: string; runnerId?: number | null; runnerNumber?: number | null }>) {
      state.isAuthenticated = true
      state.token = action.payload.token
      state.userRole = action.payload.role
      state.username = action.payload.username ?? null
      state.loggedInRunnerId = action.payload.runnerId ?? null
      state.loggedInRunnerNumber = action.payload.runnerNumber ?? null
      localStorage.setItem('auth_token', action.payload.token)
      localStorage.setItem('user_role', action.payload.role)
      if (action.payload.username) localStorage.setItem('username', action.payload.username)
      if (action.payload.runnerId != null) localStorage.setItem('runner_id', String(action.payload.runnerId))
      if (action.payload.runnerNumber != null) localStorage.setItem('runner_number', String(action.payload.runnerNumber))
    },
    logout(state) {
      state.isAuthenticated = false
      state.token = null
      state.userRole = null
      state.username = null
      state.loggedInRunnerId = null
      state.loggedInRunnerNumber = null
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_role')
      localStorage.removeItem('username')
      localStorage.removeItem('runner_id')
      localStorage.removeItem('runner_number')
      localStorage.removeItem('manual_exchange_timestamp')
    },
    setActiveTab(state, action: PayloadAction<'map' | 'team' | 'race' | 'log' | 'admin'>) {
      state.activeTab = action.payload
    },
    setNearHandoff(state, action: PayloadAction<boolean>) {
      state.isNearHandoff = action.payload
    },
    setPollingInterval(state, action: PayloadAction<5000 | 15000 | 300000>) {
      state.pollingInterval = action.payload
    },
    setTestGlow(state, action: PayloadAction<boolean>) {
      state.testGlowActive = action.payload
    },
    setTestPaceMultiplier(state, action: PayloadAction<1 | 2 | 3 | 4>) {
      state.testPaceMultiplier = action.payload
    },
    setOffline(state, action: PayloadAction<boolean>) {
      state.isOffline = action.payload
    },
    incrementPollFailures(state) {
      state.consecutivePollFailures++
      if (state.consecutivePollFailures >= 2) state.isOffline = true
    },
    resetPollFailures(state) {
      state.consecutivePollFailures = 0
      state.isOffline = false
    },
    addNotification(state, action: PayloadAction<Notification>) {
      state.notifications.push(action.payload)
    },
    removeNotification(state, action: PayloadAction<string>) {
      state.notifications = state.notifications.filter(n => n.id !== action.payload)
    },
  },
})

// Runners Slice
const runnersSlice = createSlice({
  name: 'runners',
  initialState: {
    items: [] as Runner[],
    selectedRunnerId: null as number | null,
    loading: false,
    error: null as string | null,
  },
  reducers: {
    setRunners(state, action: PayloadAction<Runner[]>) {
      state.items = action.payload
    },
    addRunner(state, action: PayloadAction<Runner>) {
      state.items.push(action.payload)
    },
    updateRunner(state, action: PayloadAction<Runner>) {
      const index = state.items.findIndex(r => r.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
    },
    deleteRunner(state, action: PayloadAction<number>) {
      state.items = state.items.filter(r => r.id !== action.payload)
    },
    setSelectedRunner(state, action: PayloadAction<number | null>) {
      state.selectedRunnerId = action.payload
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },
  },
})

// Legs Slice
const legsSlice = createSlice({
  name: 'legs',
  initialState: {
    assignments: [] as LegAssignment[],
    logs: [] as LegLog[],
    currentLegNumber: 1,
    loading: false,
    error: null as string | null,
  },
  reducers: {
    setLegAssignments(state, action: PayloadAction<LegAssignment[]>) {
      state.assignments = action.payload
    },
    updateLegAssignment(state, action: PayloadAction<LegAssignment>) {
      const index = state.assignments.findIndex(l => l.id === action.payload.id)
      if (index !== -1) {
        state.assignments[index] = action.payload
      }
    },
    setLegLogs(state, action: PayloadAction<LegLog[]>) {
      state.logs = action.payload
    },
    updateLegLog(state, action: PayloadAction<LegLog>) {
      const index = state.logs.findIndex(l => l.id === action.payload.id)
      if (index !== -1) {
        state.logs[index] = action.payload
      }
    },
    setCurrentLegNumber(state, action: PayloadAction<number>) {
      state.currentLegNumber = action.payload
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },
  },
})

// Race Slice
const raceSlice = createSlice({
  name: 'race',
  initialState: {
    status: 'not_started' as 'not_started' | 'in_progress' | 'paused' | 'completed',
    currentRunnerId: null as number | null,
    currentLegId: null as number | null,
    raceStartTime: null as string | null,
    cumulativeVan1Time: 0,
    cumulativeVan2Time: 0,
    timer: {
      isRunning: false,
      legStartTime: null as string | null,
      elapsedSeconds: 0,
    } as TimerState,
  },
  reducers: {
    setRaceStatus(state, action: PayloadAction<'not_started' | 'in_progress' | 'paused' | 'completed'>) {
      state.status = action.payload
    },
    setCurrentRunner(state, action: PayloadAction<number | null>) {
      state.currentRunnerId = action.payload
    },
    setCurrentLeg(state, action: PayloadAction<number | null>) {
      state.currentLegId = action.payload
    },
    setRaceStartTime(state, action: PayloadAction<string | null>) {
      state.raceStartTime = action.payload
    },
    startTimer(state, action: PayloadAction<string>) {
      state.timer.isRunning = true
      state.timer.legStartTime = action.payload
      state.timer.elapsedSeconds = 0
    },
    tickTimer(state) {
      if (state.timer.isRunning) {
        state.timer.elapsedSeconds++
      }
    },
    stopTimer(state) {
      state.timer.isRunning = false
    },
    resetTimer(state) {
      state.timer = {
        isRunning: false,
        legStartTime: null,
        elapsedSeconds: 0,
      }
    },
    setCumulativeVan1Time(state, action: PayloadAction<number>) {
      state.cumulativeVan1Time = action.payload
    },
    setCumulativeVan2Time(state, action: PayloadAction<number>) {
      state.cumulativeVan2Time = action.payload
    },
    addToVan1Time(state, action: PayloadAction<number>) {
      state.cumulativeVan1Time += action.payload
    },
    addToVan2Time(state, action: PayloadAction<number>) {
      state.cumulativeVan2Time += action.payload
    },
    resetCumulativeVanTimes(state) {
      state.cumulativeVan1Time = 0
      state.cumulativeVan2Time = 0
    },
  },
})

// Location Slice
const locationSlice = createSlice({
  name: 'location',
  initialState: {
    latest: {} as Record<number, LocationPoint>,
    projected: {} as Record<number, LocationPoint>,
    isProjectedMode: {} as Record<number, boolean>,
    lastUpdateTime: {} as Record<number, number>,
  },
  reducers: {
    updateLocation(state, action: PayloadAction<{ runnerId: number; location: LocationPoint }>) {
      state.latest[action.payload.runnerId] = action.payload.location
      state.lastUpdateTime[action.payload.runnerId] = Date.now()
      state.isProjectedMode[action.payload.runnerId] = false
      // Snap projected to new GPS immediately so the estimated marker doesn't lag behind
      state.projected[action.payload.runnerId] = action.payload.location
    },
    updateProjectedLocation(state, action: PayloadAction<{ runnerId: number; location: LocationPoint }>) {
      state.projected[action.payload.runnerId] = action.payload.location
    },
    setProjectedMode(state, action: PayloadAction<{ runnerId: number; isProjected: boolean }>) {
      state.isProjectedMode[action.payload.runnerId] = action.payload.isProjected
    },
    clearRunnerLocations(state, action: PayloadAction<number>) {
      delete state.latest[action.payload]
      delete state.projected[action.payload]
      delete state.isProjectedMode[action.payload]
      delete state.lastUpdateTime[action.payload]
    },
    clearAllLocations(state) {
      state.latest = {}
      state.projected = {}
      state.isProjectedMode = {}
      state.lastUpdateTime = {}
    },
  },
})

// Store
export const store = configureStore({
  reducer: {
    ui: uiSlice.reducer,
    runners: runnersSlice.reducer,
    legs: legsSlice.reducer,
    race: raceSlice.reducer,
    location: locationSlice.reducer,
  },
})

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

// Export actions
export const uiActions = uiSlice.actions
export const runnersActions = runnersSlice.actions
export const legsActions = legsSlice.actions
export const raceActions = raceSlice.actions
export const locationActions = locationSlice.actions
